import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

// Bottom-edge drag handle that resizes a chart's height. Pointer-driven (mouse
// and touch, via pointer capture) with a live preview while dragging and ONE
// commit on release; keyboard-operable as a horizontal separator (arrow keys
// step, Home resets); double-click resets.

/** Arrow-key step in px; Shift+arrow uses the large step. */
export const CHART_RESIZE_KEY_STEP = 16
export const CHART_RESIZE_KEY_STEP_LARGE = 64

export interface ChartResizeHandleProps {
  /** The committed height a drag starts from (px). */
  height: number
  min: number
  max: number
  /** Height restored by double-click / Home. */
  defaultHeight: number
  /** Every pointer move while dragging, with the clamped live height. */
  onPreview: (height: number) => void
  /**
   * Once per gesture with the final clamped height: pointer release/cancel,
   * a keyboard step, or a reset.
   */
  onCommit: (height: number) => void
  className?: string
}

interface DragState {
  pointerId: number
  startY: number
  startHeight: number
  /** The last previewed height — what the release commits. */
  current: number
}

export default function ChartResizeHandle({
  height,
  min,
  max,
  defaultHeight,
  onPreview,
  onCommit,
  className,
}: ChartResizeHandleProps) {
  const clamp = useCallback(
    (value: number) => Math.min(max, Math.max(min, Math.round(value))),
    [min, max],
  )
  const dragRef = useRef<DragState | null>(null)
  // Mirrors dragRef for rendering (the grip highlight and the px readout).
  const [previewHeight, setPreviewHeight] = useState<number | null>(null)

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Primary button only (touch and pen report 0 too).
    if (event.button !== 0 || dragRef.current) return
    event.preventDefault()
    const target = event.currentTarget
    // jsdom has no pointer capture; browsers route every move/up to the
    // handle until release even when the pointer leaves it.
    if (typeof target.setPointerCapture === "function") {
      target.setPointerCapture(event.pointerId)
    }
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: height,
      current: height,
    }
    setPreviewHeight(height)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const next = clamp(drag.startHeight + (event.clientY - drag.startY))
    if (next === drag.current) return
    drag.current = next
    setPreviewHeight(next)
    onPreview(next)
  }

  const finishDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    setPreviewHeight(null)
    const target = event.currentTarget
    if (
      typeof target.hasPointerCapture === "function" &&
      target.hasPointerCapture(event.pointerId)
    ) {
      target.releasePointerCapture(event.pointerId)
    }
    onCommit(drag.current)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey
      ? CHART_RESIZE_KEY_STEP_LARGE
      : CHART_RESIZE_KEY_STEP
    let next: number | null = null
    if (event.key === "ArrowDown") next = clamp(height + step)
    else if (event.key === "ArrowUp") next = clamp(height - step)
    else if (event.key === "Home") next = clamp(defaultHeight)
    if (next === null) return
    event.preventDefault()
    onCommit(next)
  }

  const isDragging = previewHeight !== null
  const shownHeight = previewHeight ?? height

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize chart height"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={shownHeight}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      data-testid="chart-resize-handle"
      className={cn(
        "group relative flex h-4 w-full cursor-ns-resize touch-none select-none items-center justify-center rounded-system-md outline-none focus-visible:ring-2 focus-visible:ring-system-blue-400",
        className,
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={finishDrag}
      onDoubleClick={() => onCommit(clamp(defaultHeight))}
      onKeyDown={handleKeyDown}
    >
      <span
        aria-hidden
        className={cn(
          "h-1.5 w-20 rounded-full transition-colors",
          isDragging
            ? "bg-system-blue-200"
            : "bg-system-blue-500 group-hover:bg-system-blue-300 group-focus-visible:bg-system-blue-300",
        )}
      />
      {isDragging && (
        <span
          aria-hidden
          className="absolute right-0 top-1/2 -translate-y-1/2 rounded-system-md bg-system-blue-700 px-2 py-0.5 text-xs tabular-nums text-system-blue-200"
        >
          {shownHeight}px
        </span>
      )}
    </div>
  )
}
